/**
 * Fixture 3D (ELECTRICAL FIXTURES round, 2026-08-12): simple PROCEDURAL bodies —
 * cover plates for outlet/switch/low-voltage, an honest backplate + lamp mass for
 * the sconce — instanced per TYPE through the SAME pipeline as doors/windows/
 * casework/objects (protoKey → one prototype + a transform per element; every
 * body-shaping param is a TYPE param, so the plain type key forks correctly).
 *
 * SURFACE-MOUNTED, non-cutting: the wall generator never sees a fixture (no
 * `cutsHost`) — the body simply sits ON the mounted face at the resolved mount
 * height (fixtureKinds.ts#fixtureMountHeightFt — the plain instance value, seeded
 * from the kind's code table at creation; heights measure to device CENTER). It
 * appears at true height in sections/interior elevations through the generic
 * instanced emission (section.ts), exactly like casework.
 *
 * The PLAN never reads this geometry — the authored symbol (planSymbols.ts) is the
 * only plan representation, so fixtureSolid emits NO projLines and no outline prism.
 *
 * Prototype local frame (fixtureKinds.ts convention): x across the plate width
 * centered on 0, y = depth 0..d off the wall face into the room, z spanning the
 * body height CENTERED on 0 (the instance lands at the mount-height center, so the
 * proto is symmetric about its own anchor).
 *
 * CEILING kinds (recessedCan/pendant/flushMount — CEILING HOSTING round,
 * 2026-08-23, moved in from the `object` category): a DIFFERENT local frame —
 * x/y centered on 0 (the true anchor, not a wall-adjacent point), z=0 is the
 * CEILING ATTACH POINT. Only the RECESSED can builds UP into the joist bay;
 * pendant and flushMount hang DOWN below the ceiling (negative z span —
 * flushMount corrected 2026-08-24, it's surface-mounted). These host into a
 * ceiling element instead of a wall — fixtureSolid branches on kind.
 */
import type { ParamValue, Path2D, Pt2, ResolvedElement } from '@aj/bim-kernel'
import {
  fixtureBodyDims, fixtureMountHeightFt, isCantedWallParams, isCeilingFixtureKind, objectAnchorPoint,
  UNHOSTED_CEILING_LIGHT_FT, isGroundFixtureKind, isStyledLightKind, lightStyleOf, lightStyleZSpanFt, recessedCanRecess,
  switchDeviceOf, switchGangs, gangCentersFt,
  ceilingSlopePlane, ceilingSlopeRiseAt,
} from '@aj/bim-kernel'
import { buildLightStyleProto } from './lightStyles3d'
import { CEILING_TILT_PARAM, ceilingTiltOf, localCeilingTilt, tiltForCeiling } from './ceilingTilt'
import { MeshBuilder } from '../../extrude'
import { featureEdges } from '../../edges'
import type { OpeningProto } from '../opening/protos'
import type { ElementSolid } from '../wall/generate3d'
import { fixtureFacePlacement } from './planSymbols'

/**
 * The fixture's vertical extent. Wall kinds: RELATIVE to the host wall base
 * (mount = center). CEILING kinds (CEILING HOSTING round, 2026-08-23): RELATIVE
 * to the host ceiling's own z — local z=0 IS the ceiling attach point for every
 * kind. Only the RECESSED can builds UP into the joist bay; pendant hangs DOWN
 * to its shade, and flushMount hangs DOWN too (2026-08-24 fix, Matt: "a flush
 * mount extends down from the surface of the ceiling, doesn't it? It looks
 * like a big recessed light in the model" — the category move had kept the old
 * object-frame build-up coordinates, poking the drum INTO the ceiling with
 * only the diffuser face flush, which read as a recessed aperture).
 */
export function fixtureZSpan(
  params: Record<string, ParamValue>,
  typeParams: Record<string, ParamValue>,
): { z0: number; z1: number } {
  const kind = String(typeParams.fixtureKind || 'outlet')
  // LIGHT STYLES (2026-10-07): a styled light spans its STYLE's body — about the mount point
  // (wall kinds), below the ceiling (ceiling kinds), or up from its base (ground kinds, whose
  // Base Height lifts the whole span off the level)
  const merged = { ...typeParams, ...params }
  const style = lightStyleOf(kind, merged)
  if (style) {
    const sp = lightStyleZSpanFt(kind, style, merged)
    if (isGroundFixtureKind(kind)) {
      const base = (params.baseOffsetFt as number) || 0
      return { z0: base + sp.z0, z1: base + sp.z1 }
    }
    if (isCeilingFixtureKind(kind)) return sp
    const mount = fixtureMountHeightFt(params, typeParams)
    return { z0: mount + sp.z0, z1: mount + sp.z1 }
  }
  if (isCeilingFixtureKind(kind)) {
    const h = (typeParams.heightFt as number) || 2.5
    // the recessed can's trim flange sits just proud of the ceiling (recessedCanRecess)
    return kind === 'recessedCan' ? { z0: -0.012, z1: h } : { z0: -h, z1: 0 }
  }
  const mount = fixtureMountHeightFt(params, typeParams)
  const h = fixtureBodyDims(kind, typeParams).heightFt
  return { z0: mount - h / 2, z1: mount + h / 2 }
}

export function buildFixtureProto(el: ResolvedElement, key: string): OpeningProto {
  const p = el.typeParams as Record<string, ParamValue>
  const kind = String(p.fixtureKind || 'outlet')
  // LIGHT STYLES (2026-10-07): every styled light is built by its style (lightStyles3d.ts)
  if (isStyledLightKind(kind)) return buildLightStyleProto(el, key)
  // CEILING kinds size off their OWN type params (a curated 2"/4"/6" trim
  // diameter etc. — the objectKinds.ts convention carried over), not the flat
  // per-kind FIXTURE_BODY_DIMS table every wall kind uses.
  // GANGED SWITCHES: a switch's gang count + device are per instance (protoKeyFor forks on them)
  const sp = kind === 'switch' ? { ...p, ...(el.params as Record<string, ParamValue>) } : p
  const { widthFt: w, heightFt: h, depthFt: d } = isCeilingFixtureKind(kind)
    ? { widthFt: (p.widthFt as number) || 2, heightFt: (p.heightFt as number) || 2.5, depthFt: (p.depthFt as number) || 2 }
    : fixtureBodyDims(kind, sp)

  const b = new MeshBuilder()
  const box = (x0: number, x1: number, y0: number, y1: number, zA: number, zB: number) => {
    if (x1 - x0 < 1e-4 || y1 - y0 < 1e-4 || zB - zA < 1e-4) return
    prism([[x0, y0], [x1, y0], [x1, y1], [x0, y1]], zA, zB)
  }
  /** vertical prism over a convex CCW polygon — sides + top/bottom fans (the
   *  object-proto helper, same local conventions) */
  const prism = (poly: Pt2[], zA: number, zB: number) => {
    const n = poly.length
    const bot = poly.map((q) => [q[0], q[1], zA])
    const top = poly.map((q) => [q[0], q[1], zB])
    for (let i = 2; i < n; i++) {
      b.addTri(top[0], top[i - 1], top[i], [0, 0, 1])
      b.addTri(bot[0], bot[i], bot[i - 1], [0, 0, -1])
    }
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      const nx = poly[j][1] - poly[i][1], ny = poly[i][0] - poly[j][0]
      const len = Math.hypot(nx, ny) || 1
      const nn = [nx / len, ny / len, 0]
      b.addTri(bot[i], bot[j], top[j], nn)
      b.addTri(bot[i], top[j], top[i], nn)
    }
  }
  const ellipsePoly = (cx: number, cy: number, rx: number, ry: number, segs = 12): Pt2[] => {
    const pts: Pt2[] = []
    for (let i = 0; i < segs; i++) {
      const a = (Math.PI * 2 * i) / segs
      pts.push([cx + Math.cos(a) * rx, cy + Math.sin(a) * ry])
    }
    return pts
  }

  /** an OPEN tube between two matching rings (outer CCW, inner the same vertex count) —
   *  inner wall faces in, outer wall out, bottom annulus down, top annulus up. The recessed
   *  can's flange and baffle: the hole they line stays open so you look up into the can. */
  const ringPrism = (outer: Pt2[], inner: Pt2[], zA: number, zB: number, capTop = true) => {
    const n = outer.length
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      const oA = [outer[i][0], outer[i][1]], oB = [outer[j][0], outer[j][1]]
      const iA = [inner[i][0], inner[i][1]], iB = [inner[j][0], inner[j][1]]
      // outer wall
      let nx = oB[1] - oA[1], ny = oA[0] - oB[0], len = Math.hypot(nx, ny) || 1
      const no = [nx / len, ny / len, 0]
      b.addTri([oA[0], oA[1], zA], [oB[0], oB[1], zA], [oB[0], oB[1], zB], no)
      b.addTri([oA[0], oA[1], zA], [oB[0], oB[1], zB], [oA[0], oA[1], zB], no)
      // inner wall (facing the axis)
      nx = iA[1] - iB[1]; ny = iB[0] - iA[0]; len = Math.hypot(nx, ny) || 1
      const ni = [nx / len, ny / len, 0]
      b.addTri([iA[0], iA[1], zA], [iB[0], iB[1], zB], [iB[0], iB[1], zA], ni)
      b.addTri([iA[0], iA[1], zA], [iA[0], iA[1], zB], [iB[0], iB[1], zB], ni)
      // bottom annulus (down)
      b.addTri([oA[0], oA[1], zA], [iB[0], iB[1], zA], [oB[0], oB[1], zA], [0, 0, -1])
      b.addTri([oA[0], oA[1], zA], [iA[0], iA[1], zA], [iB[0], iB[1], zA], [0, 0, -1])
      if (capTop) {
        b.addTri([oA[0], oA[1], zB], [oB[0], oB[1], zB], [iB[0], iB[1], zB], [0, 0, 1])
        b.addTri([oA[0], oA[1], zB], [iB[0], iB[1], zB], [iA[0], iA[1], zB], [0, 0, 1])
      }
    }
  }
  const squarePoly = (r: number): Pt2[] => [[-r, -r], [r, -r], [r, r], [-r, r]]
  /** a box between two points (its long axis), `wx` across x and `wn` across the other
   *  axis — the switch's tilted toggle lever. Faces wound outward off the box centre. */
  const strut = (p0: number[], p1: number[], wx: number, wn: number) => {
    const ax = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]]
    const al = Math.hypot(ax[0], ax[1], ax[2]) || 1
    const en = [0, -ax[2] / al, ax[1] / al]   // perpendicular in the y-z plane
    const c = (q: number[], sx: number, sn: number) => [q[0] + sx * wx / 2, q[1] + sn * en[1] * wn / 2, q[2] + sn * en[2] * wn / 2]
    const v = [c(p0, -1, -1), c(p0, 1, -1), c(p0, 1, 1), c(p0, -1, 1), c(p1, -1, -1), c(p1, 1, -1), c(p1, 1, 1), c(p1, -1, 1)]
    const mid = [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2, (p0[2] + p1[2]) / 2]
    for (const f of [[0, 1, 2, 3], [4, 7, 6, 5], [0, 4, 5, 1], [1, 5, 6, 2], [2, 6, 7, 3], [3, 7, 4, 0]]) {
      let [a, bb, cc, dd] = f.map((i) => v[i])
      const u1 = [bb[0] - a[0], bb[1] - a[1], bb[2] - a[2]], u2 = [cc[0] - a[0], cc[1] - a[1], cc[2] - a[2]]
      let n = [u1[1] * u2[2] - u1[2] * u2[1], u1[2] * u2[0] - u1[0] * u2[2], u1[0] * u2[1] - u1[1] * u2[0]]
      const fc = [(a[0] + cc[0]) / 2 - mid[0], (a[1] + cc[1]) / 2 - mid[1], (a[2] + cc[2]) / 2 - mid[2]]
      if (n[0] * fc[0] + n[1] * fc[1] + n[2] * fc[2] < 0) { [bb, dd] = [dd, bb]; n = n.map((x) => -x) }
      const nl = Math.hypot(n[0], n[1], n[2]) || 1
      n = n.map((x) => x / nl)
      b.addTri(a, bb, cc, n)
      b.addTri(a, cc, dd, n)
    }
  }

  switch (kind) {
    case 'sconce': {
      // backplate against the wall + the lamp mass standing off it
      box(-w * 0.3, w * 0.3, 0, d * 0.25, -h / 2, h / 2)                     // backplate
      prism(ellipsePoly(0, d * 0.6, w / 2, d * 0.35), -h * 0.28, h * 0.28)  // lamp body
      break
    }
    case 'switch': {
      box(-w / 2, w / 2, 0, d, -h / 2, h / 2)                                // cover plate
      // GANGED SWITCHES (2026-10-14): one device per gang, side by side at the plate pitch
      const dev = switchDeviceOf(sp).value
      const bw = 2.75 / 12   // a single gang's plate width — sizes each device
      for (const gx of gangCentersFt(switchGangs(sp))) {
        if (dev === 'standard') {
          box(gx - bw * 0.15, gx + bw * 0.15, d, d + 0.008, -h * 0.1, h * 0.1)  // toggle bushing
          // the toggle lever, DOWN = off (2026-10-12, Matt: "when the switch is on vs. off in
          // walkaround mode, I'd like to be able to tell when looking at the switch") — a walk
          // that turns its lights on swaps in the UP lever (sceneStore SWITCH_LEVER)
          const tilt = (32 * Math.PI) / 180
          strut([gx, d + 0.004, 0], [gx, d + 0.004 + 0.078 * Math.cos(tilt), -0.078 * Math.sin(tilt)], 0.03, 0.022)
        } else if (dev === 'keypad') {
          // a column of scene buttons
          for (let k = 0; k < 4; k++) {
            const zc = h * 0.27 - k * h * 0.15
            box(gx - bw * 0.2, gx + bw * 0.2, d, d + 0.01, zc - h * 0.05, zc + h * 0.05)
          }
        } else {
          // stacked rockers — two or three in one decorator opening
          const n = dev === 'stack3' ? 3 : 2
          const span = h * 0.5, pitch = span / n
          for (let k = 0; k < n; k++) {
            const zc = span / 2 - pitch * (k + 0.5)
            box(gx - bw * 0.22, gx + bw * 0.22, d, d + 0.012, zc - pitch * 0.42, zc + pitch * 0.42)
          }
        }
      }
      break
    }
    case 'lowVoltage': {
      box(-w / 2, w / 2, 0, d, -h / 2, h / 2)                                // plain plate
      break
    }
    // ---- FIT-OUT CATALOG (2026-09-30): bath accessories + electrical equipment.
    // Local frame as above: x along the wall (centered), y off the face, z centered
    // on the mount height. Honest simple masses (the procedural-tier doctrine). ----
    case 'grabBar':
    case 'towelBar':
    case 'toiletPaperHolder': {
      // two end flanges on the wall + the bar standing off them
      const fl = Math.min(0.2, w * 0.15), bar = Math.min(h, 0.11)
      box(-w / 2, -w / 2 + fl, 0, d * 0.3, -h / 2, h / 2)
      box(w / 2 - fl, w / 2, 0, d * 0.3, -h / 2, h / 2)
      box(-w / 2 + fl * 0.3, w / 2 - fl * 0.3, d * 0.3, d * 0.3 + bar, -bar / 2, bar / 2)
      break
    }
    case 'towelRing': {
      box(-0.1, 0.1, 0, d * 0.4, h / 2 - 0.12, h / 2)                        // mount
      box(-w / 2, w / 2, d * 0.4, d, -h / 2, -h / 2 + 0.06)                  // ring bottom
      box(-w / 2, -w / 2 + 0.05, d * 0.4, d, -h / 2, h / 2 - 0.12)           // ring sides
      box(w / 2 - 0.05, w / 2, d * 0.4, d, -h / 2, h / 2 - 0.12)
      break
    }
    case 'robeHook': {
      box(-w / 2, w / 2, 0, d * 0.25, -h / 2, h / 2)                         // rosette
      box(-w * 0.2, w * 0.2, d * 0.25, d, -h * 0.1, h * 0.25)                // hook
      break
    }
    case 'mirror': {
      box(-w / 2, w / 2, 0, d, -h / 2, h / 2)                                // glass + backing
      break
    }
    case 'medicineCabinet': {
      box(-w / 2, w / 2, 0, d - 0.03, -h / 2, h / 2)                         // box
      box(-w / 2 + 0.03, w / 2 - 0.03, d - 0.03, d, -h / 2 + 0.03, h / 2 - 0.03)  // mirrored door
      break
    }
    case 'panel':
    case 'subpanel':
    case 'meter':
    case 'evCharger':
    case 'inverter':
    case 'disconnect':
    case 'thermostat': {
      box(-w / 2, w / 2, 0, d - 0.02, -h / 2, h / 2)                         // enclosure
      box(-w / 2 + 0.03, w / 2 - 0.03, d - 0.02, d, -h / 2 + 0.03, h / 2 - 0.03)  // door / face
      break
    }
    // ---- CEILING HOSTING round (2026-08-23): ceiling-light bodies, moved in
    // from the `object` category. Local z=0 is the ceiling attach point for
    // every kind here (see the header note) — only the RECESSED can builds UP
    // into the joist bay; pendant AND flushMount hang DOWN below the ceiling
    // (flushMount corrected 2026-08-24: it's SURFACE-mounted — the drum sits
    // against the ceiling face, not inside it). ----
    case 'recessedCan': {
      // RECESSED CAN RECESS (2026-10-12, Matt: "recessed lights don't actually cut themselves
      // into the ceiling and instead just show overlapping it"): the ceiling's aperture hole is
      // the trim radius r (slab/generate3d.ts#recessedApertures). Before, a SOLID trim disc sat
      // flush in that hole, its face exactly on the ceiling plane — the can read as a sticker,
      // and the lit glow (on the same plane) flickered against it. Now a thin flange laps the
      // hole's edge just proud of the ceiling, an open baffle lines the hole, and the lamp sits
      // set back up inside it, where the glow lands (kernel recessedCanRecess).
      const r = Math.min(w, d) / 2
      const rc = recessedCanRecess(r)
      const sq = String(p.trimShape) === 'square'
      const ring = (rad: number) => (sq ? squarePoly(rad) : ellipsePoly(0, 0, rad, rad, 20))
      ringPrism(ring(rc.flangeOuterFt), ring(rc.baffleInnerFt), -rc.flangeDropFt, 0)   // trim flange
      ringPrism(ring(r * 0.98), ring(rc.baffleInnerFt), 0, rc.lensZ, false)             // baffle
      prism(ring(rc.baffleInnerFt), rc.lensZ, rc.lensZ + 0.02)                          // lamp / lens
      prism(ring(r * 0.98), rc.lensZ + 0.02, Math.max(h, rc.lensZ + 0.06))             // housing
      break
    }
    case 'pendant': {
      // canopy at the ceiling (z=0) down to a conical-read shade (two stacked
      // frustum stand-ins: a narrow stem + the shade drum — honest simple masses)
      const r = Math.min(w, d) / 2
      prism(ellipsePoly(0, 0, 0.02, 0.02, 6), -h * 0.65, 0)             // cord/stem
      prism(ellipsePoly(0, 0, r * 0.45, r * 0.45, 12), -h * 0.75, -h * 0.65)  // neck
      prism(ellipsePoly(0, 0, r, r, 14), -h, -h * 0.75)                 // shade drum
      break
    }
    case 'flushMount': {
      // body drum against the ceiling face + slimmer diffuser lip hanging below —
      // the whole fixture extends DOWN from z=0 (2026-08-24 fix, Matt: "a flush
      // mount extends down from the surface of the ceiling")
      const r = Math.min(w, d) / 2
      prism(ellipsePoly(0, 0, r, r, 14), -h * 0.7, 0)                   // body drum
      prism(ellipsePoly(0, 0, r * 0.8, r * 0.8, 14), -h, -h * 0.7)      // diffuser
      break
    }
    // ---- CEILING DEVICES (bim-plan/28 round 3): a detector is a low disc on the ceiling, a
    // register a thin grille plate with a few blades — both hang DOWN from z=0 ----
    case 'smokeDetector':
    case 'coDetector': {
      const r = Math.min(w, d) / 2
      prism(ellipsePoly(0, 0, r, r, 16), -h * 0.6, 0)
      prism(ellipsePoly(0, 0, r * 0.7, r * 0.7, 16), -h, -h * 0.6)
      break
    }
    case 'supplyRegister':
    case 'returnRegister': {
      const t = Math.max(0.015, h * 0.4)
      box(-w / 2, w / 2, -d / 2, d / 2, -t, 0)                            // frame plate
      const n = Math.max(2, Math.round(d / 0.12))
      for (let k = 1; k < n; k++) {
        const y = -d / 2 + (d * k) / n
        box(-w / 2 + 0.03, w / 2 - 0.03, y - 0.008, y + 0.008, -h, -t)    // blades
      }
      break
    }
    default: {
      // duplex outlet: cover plate + the two receptacle bumps
      box(-w / 2, w / 2, 0, d, -h / 2, h / 2)
      for (const zc of [-h * 0.22, h * 0.22]) {
        prism(ellipsePoly(0, d + 0.008, w * 0.2, 0.008, 10), zc - h * 0.14, zc + h * 0.14)
      }
      break
    }
  }

  // SLOPED CEILINGS: square to the ceiling (a hanging kind: its canopy band only)
  const tilt = isCeilingFixtureKind(kind) ? ceilingTiltOf(el.params as Record<string, ParamValue>) : null
  if (tilt) tiltForCeiling(b, tilt, kind)
  const solid = b.build()
  const sm = el.typeParams.surfaceMaterial
  return {
    key, solid, glass: null, edges: featureEdges(solid),
    solidMaterialId: typeof sm === 'string' && sm ? sm : null,
  }
}

/**
 * The per-element solid: meshless — the body rides the instancing pipeline via
 * `openingInstances`, placed on the host wall's mounted face at the resolved mount
 * height (the caseworkSolid pattern). NO projLines: the plan draws the authored
 * symbol, never this geometry.
 */
export function fixtureSolid(el: ResolvedElement, host: ResolvedElement | null): ElementSolid | null {
  const kind = String(el.typeParams.fixtureKind || 'outlet')
  // CEILING HOSTING round (2026-08-23): recessedCan/pendant/flushMount host
  // into a ceiling — a 2D area, not a path, so this uses the OBJECT anchor
  // model (`path` + `rotationDeg`) instead of the wall face-placement math
  // below. z derives from the HOST CEILING's own resolved z (never stored) —
  // local z=0 is the ceiling attach point (fixtureZSpan's header note).
  // LIGHT STYLES (2026-10-07): ground lights (post / pier lanterns, path lights) — the point
  // anchor, no host, the base at the level plus its own Base Height
  if (isGroundFixtureKind(kind)) {
    const anchor = objectAnchorPoint(el)
    if (!anchor || !el.level) return null
    const zLevel = el.level.elevationFt
    const rotZ = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
    const { z0, z1 } = fixtureZSpan(el.params, el.typeParams)
    return {
      elementId: el.id,
      category: 'fixture',
      outlines: [],
      zBase: zLevel + z0,
      zTop: zLevel + z1,
      materialId: 'default',
      groupKey: `fixture|${el.levelId}|${el.id}`,
      pieces: [],
      placements: [],
      openingInstances: [{ opening: el, pos: [anchor[0], anchor[1], zLevel + ((el.params.baseOffsetFt as number) || 0)], rotZ }],
      meshOnly: true,
    }
  }
  if (isCeilingFixtureKind(kind)) {
    const anchor = objectAnchorPoint(el)
    if (!anchor) return null
    const ceiling = host && host.category === 'ceiling' && host.level ? host : null
    if (!ceiling && !el.level) return null
    // UNHOSTED fallback (2026-08-24 migration hardening): a light migrated in a
    // model with no ceiling drawn yet still renders, at the standard height off
    // its own level, until the per-open re-adopt pass attaches a real ceiling.
    // SLOPED CEILINGS (2026-11-02, Matt): the light sits on the ceiling's plane AT ITS OWN
    // SPOT (it used to sit at the ceiling's base height, floating below or buried in a sloped
    // one), and what mounts to the ceiling tilts with it (ceilingTilt.ts)
    const plane = ceiling ? ceilingSlopePlane(ceiling.params.boundary as Path2D | null) : null
    const zBase = ceiling
      ? ceiling.level!.elevationFt + ((ceiling.params.heightOffsetFt as number) || 0) + ceilingSlopeRiseAt(plane, anchor)
      : el.level!.elevationFt + UNHOSTED_CEILING_LIGHT_FT
    const rotZ = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
    const { z0, z1 } = fixtureZSpan(el.params, el.typeParams)
    const tilt = plane ? localCeilingTilt(plane.m, plane.n, rotZ) : null
    const opening = tilt ? { ...el, params: { ...el.params, [CEILING_TILT_PARAM]: tilt as unknown as ParamValue } } : el
    return {
      elementId: el.id,
      category: 'fixture',
      outlines: [],
      zBase: zBase + z0,
      zTop: zBase + z1,
      materialId: 'default',
      groupKey: `fixture|${el.levelId}|${el.id}`,
      pieces: [],
      placements: [],
      openingInstances: [{ opening, pos: [anchor[0], anchor[1], zBase], rotZ }],
      meshOnly: true,
    }
  }
  if (!host || host.category !== 'wall' || !host.level) return null
  // CANTED WALLS round (docs/bim-plan/15 §7 Q6): fixtures refuse canted hosts —
  // face placement is height-blind, so a sconce at 4' would float off a battered
  // face by mountHeight·tan(cant). Face-following placement is the future fix.
  if (isCantedWallParams(host.params)) return null
  const place = fixtureFacePlacement(host, (el.params.hostOffsetFt as number) || 0, el.params.sideFlip === true)
  if (!place) return null
  const zBase = host.level.elevationFt + ((host.params.baseOffsetFt as number) || 0)
  const { z0, z1 } = fixtureZSpan(el.params, el.typeParams)
  // the prototype is built about the MOUNT point (the box centre) — for the symmetric device
  // bodies that is their centre too; a styled light hangs / rises off it as it really does
  const zCenter = zBase + fixtureMountHeightFt(el.params, el.typeParams)
  return {
    elementId: el.id,
    category: 'fixture',
    outlines: [],
    zBase: zBase + z0,
    zTop: zBase + z1,
    materialId: 'default',
    groupKey: `fixture|${el.levelId}|${el.id}`,
    pieces: [],
    placements: [],
    openingInstances: [{ opening: el, pos: [place.pt[0], place.pt[1], zCenter], rotZ: place.rotZ }],
    meshOnly: true,
  }
}
